import type { Channel } from './line-item-channels';

export type ZohoDefaultsSelection = {
  channelId?: number;
  tacticId?: number;
  priceTypeId?: number;
  platformIds?: number[];
};

export type ZohoAmbiguityField = 'channel' | 'tactic' | 'priceType' | 'platforms';

export type ZohoAmbiguity = {
  field: ZohoAmbiguityField;
  candidates: Array<{ id: number; name: string }>;
  reason: string;
};

export type ZohoDefaultsResult = {
  defaults: ZohoDefaultsSelection;
  ambiguities: ZohoAmbiguity[];
};

export function resolveZohoDefaults(
  canonicalPlatformIds: number[],
  channels: Channel[]
): ZohoDefaultsResult {
  const hasIntent = canonicalPlatformIds.length > 0;
  const intentSet = new Set(canonicalPlatformIds);

  const matchingTactics = channels.flatMap(ch =>
    ch.tactics
      .filter(t => t.platforms.some(p => intentSet.has(p.id)))
      .map(t => ({ channel: ch, tactic: t }))
  );

  const candidateChannels = uniqueById(matchingTactics.map(m => m.channel));
  const candidateTactics = uniqueById(matchingTactics.map(m => m.tactic));
  const noMatch = hasIntent && matchingTactics.length === 0;

  const defaults: ZohoDefaultsSelection = {};
  const ambiguities: ZohoAmbiguity[] = [];

  if (candidateChannels.length === 1) {
    defaults.channelId = candidateChannels[0].id;
  } else {
    ambiguities.push({
      field: 'channel',
      candidates: hasIntent
        ? candidateChannels.map(c => ({ id: c.id, name: c.name }))
        : channels.map(c => ({ id: c.id, name: c.name })),
      reason: hasIntent
        ? 'Zoho platforms span multiple channels'
        : 'Zoho intent missing — pick a channel'
    });
  }

  if (candidateTactics.length === 1) {
    defaults.tacticId = candidateTactics[0].id;
  } else {
    ambiguities.push({
      field: 'tactic',
      candidates: candidateTactics.map(t => ({ id: t.id, name: t.name })),
      reason: hasIntent
        ? 'Zoho platforms map to multiple tactics'
        : 'Zoho intent missing — pick a tactic'
    });
  }

  if (defaults.tacticId !== undefined) {
    const tactic = candidateTactics[0];
    const platformIds = tactic.platforms.filter(p => intentSet.has(p.id)).map(p => p.id);
    if (platformIds.length > 0) defaults.platformIds = platformIds;

    const priceTypes = uniqueById(tactic.rates.map(r => r.unitPriceType));
    if (priceTypes.length === 1) {
      defaults.priceTypeId = priceTypes[0].id;
    } else if (priceTypes.length > 1) {
      ambiguities.push({
        field: 'priceType',
        candidates: priceTypes.map(p => ({ id: p.id, name: p.name })),
        reason: 'Tactic supports multiple price types'
      });
    }
  } else {
    ambiguities.push({
      field: 'priceType',
      candidates: [],
      reason: 'Pick a tactic first'
    });
    ambiguities.push({
      field: 'platforms',
      candidates: [],
      reason: noMatch
        ? 'Zoho platforms do not match any tactic in the rate card — pick manually'
        : !hasIntent
          ? 'Zoho intent missing — pick a tactic first'
          : 'Pick a tactic first'
    });
  }

  return { defaults, ambiguities };
}

function uniqueById<T extends { id: number }>(items: T[]): T[] {
  const seen = new Set<number>();
  const out: T[] = [];
  for (const item of items) {
    if (!seen.has(item.id)) {
      seen.add(item.id);
      out.push(item);
    }
  }
  return out;
}
